/**
 * PRAETORI PLATFORM ADMIN
 *
 * is_platform_admin sits above the CrewBit role hierarchy.
 * Current capabilities:
 * - Industry lens switcher (preview any industry's UI config)
 * - Full operational access (no restrictions)
 *
 * Future capabilities (unified Praetori console):
 * - Cross-business view (see all CrewBit businesses)
 * - Sentinel integration (security ops alongside CrewBit)
 * - Support tools (impersonate user, view activity, resolve issues)
 * - Feature flags (enable/disable features per business)
 * - Analytics dashboard (platform-wide metrics)
 *
 * SQL to enable (run manually in Supabase SQL Editor):
 *   ALTER TABLE public.users ADD COLUMN is_platform_admin boolean DEFAULT false;
 *   UPDATE public.users SET is_platform_admin = true WHERE email = '<nick email>';
 */

import { create } from 'zustand';
import { getScheduleConfig, type ScheduleConfig } from '../data/scheduleConfig';
import { getIndustryLabel } from '../data/industryTaxonomy';

interface PlatformState {
  industryLensActive: boolean;
  lensIndustryId: string | null;
  lensIndustryLabel: string | null;

  setIndustryLens: (industryId: string | null) => void;
  clearLens: () => void;

  /** Returns lens value if active, otherwise the real business value */
  getEffectiveCategoryId: (realCategoryId: string | null | undefined) => string | null;
  /** Get schedule config for the effective industry */
  getEffectiveScheduleConfig: (realCategoryId: string | null | undefined) => ScheduleConfig;
}

export const usePlatformStore = create<PlatformState>((set, get) => ({
  industryLensActive: false,
  lensIndustryId: null,
  lensIndustryLabel: null,

  setIndustryLens: (industryId: string | null) => {
    if (!industryId) {
      set({ industryLensActive: false, lensIndustryId: null, lensIndustryLabel: null });
    } else {
      set({ industryLensActive: true, lensIndustryId: industryId, lensIndustryLabel: getIndustryLabel(industryId) });
    }
  },

  clearLens: () => {
    set({ industryLensActive: false, lensIndustryId: null, lensIndustryLabel: null });
  },

  getEffectiveCategoryId: (realCategoryId) => {
    const { industryLensActive, lensIndustryId } = get();
    return industryLensActive && lensIndustryId ? lensIndustryId : (realCategoryId ?? null);
  },

  getEffectiveScheduleConfig: (realCategoryId) => {
    const effectiveId = get().getEffectiveCategoryId(realCategoryId);
    return getScheduleConfig(effectiveId);
  },
}));
